import type { BadgeColor } from '@/components/Badge';

export type FilterType =
  | 'text'
  | 'enum'
  | 'badge-enum'
  | 'numeric'
  /**
   * Date range. `numeric` guesses dates from the loaded rows, so an empty list showed number inputs
   * and sent a number as the date.
   */
  | 'date'
  | 'accounting-category'
  /** Free text (« contient ») — for columns with too many distinct values for a checkbox list. */
  | 'search'
  | 'none';

/** The filter types whose dropdown lists every distinct value of the column. */
export const CHECKBOX_FILTER_TYPES: readonly FilterType[] = ['text', 'enum', 'badge-enum'];

export interface FilterOption<T = string | number | boolean> {
  value: T;
  label: string;
}

/**
 * How a paginated table filters. The table only ever holds a slice of its rows, so a filter or an
 * option list built from that slice silently lies about everything further down.
 *
 * - `server`: every column filter is sent with the query (the table never filters its slice
 *   itself) and every checkbox filter declares its complete option list (`filterOptions` or
 *   `useFilterOptions`) — the dev build throws otherwise.
 * - `load-all`: filtering stays in the table; before a checkbox filter opens or a filter applies,
 *   the table pulls every remaining page so filters and options see the whole list. Reserve it
 *   for lists that stay in the hundreds.
 */
export type PaginatedFiltering = 'server' | 'load-all';

export interface TablePagination {
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  filtering: PaginatedFiltering;
}

export interface BadgeOption<T = string | number> {
  value: T;
  label: string;
  color: BadgeColor;
  /**
   * The facet the option belongs to, on a column that packs several (type, statut, état…): the
   * filter lists options under their facet and keeps the rows matching every facet ticked.
   */
  group?: string;
}

export interface RangeFilterValue {
  min?: number | null;
  max?: number | null;
}

export interface DateRangeFilterValue {
  min?: Date | null;
  max?: Date | null;
}

export type NumericFilterValue = RangeFilterValue | DateRangeFilterValue;

export interface TableColumnMeta<TData = unknown, T = string | number | boolean> {
  filterType?: FilterType;
  options?: BadgeOption<T>[];
  /**
   * The full value list of a checkbox filter. Without it the list is built from the rows loaded so
   * far, which on a paginated table misses every value that only appears further down.
   */
  filterOptions?: FilterOption<T>[];
  /**
   * Same as `filterOptions`, as a hook: for a list that comes from its own query (every compte of
   * the CSE…). Called by the filter dropdown, so it may use any React hook; `undefined` while
   * loading.
   */
  useFilterOptions?: () => FilterOption<T>[] | undefined;
  /** Filter applied by default (and restored by « Réinitialiser les filtres »): a value list, or a range for numeric/date columns. */
  defaultFilter?: unknown[] | NumericFilterValue;
  getFilterValue?: (row: TData) => unknown | unknown[];
}
